/**
 * Lightbox — full-screen image overlay shared across every case study.
 *
 * Mounted into a singleton root on <body> by bootstrap.jsx. Driven by clicks
 * intercepted on any <img> inside a [data-jt-island^="case-study-"] container.
 *
 * Modes:
 *  - Fit (default): image at object-contain, max 92vh × 92vw — never larger
 *    than its natural resolution. Tall images become narrow because they get
 *    scaled down to fit viewport height.
 *  - Zoomed (click-to-toggle): image renders at its natural width inside a
 *    scrollable container, so users can read fine print on long page
 *    screenshots. Click again to return to Fit. The mouse cursor changes
 *    between zoom-in / zoom-out depending on the current mode. Added
 *    2026-05-06 for Advisent's tall page-screenshot tiles.
 *
 * Close: Escape key, backdrop click, or close button (top-right pill).
 * Body scroll lock while open.
 * Subtle scale/opacity entry + exit.
 */

import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

function CloseIcon() {
	return (
		<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
			<path d="M18 6 6 18" />
			<path d="m6 6 12 12" />
		</svg>
	);
}

/**
 * Transparent artwork needs a backdrop it can actually be seen against.
 *
 * Source images keep their alpha (Les, 2026-09-18 — flattening a PNG onto white
 * puts a hard white box around mockups on the cream page). But a 93%-transparent
 * logo sheet full of black linework is invisible on this overlay's near-black
 * backdrop, which is exactly why those files used to get baked onto white.
 *
 * So: sample the image on open and decide. Downscale to <=80px, measure how much
 * of it is transparent and how bright the visible pixels are. Transparent + dark
 * artwork gets a white canvas hugging the image; everything else (photos, screen-
 * shots, white-on-transparent art designed for dark) is left alone. Same-origin
 * uploads, so the canvas never taints. Any failure falls back to no canvas, i.e.
 * exactly the previous behavior.
 */
function useNeedsLightCanvas( src ) {
	const [ needsLight, setNeedsLight ] = useState( false );

	useEffect( () => {
		if ( ! src ) { setNeedsLight( false ); return undefined; }
		let cancelled = false;
		const img = new Image();
		img.decoding = 'async';
		img.onload = () => {
			if ( cancelled ) return;
			try {
				const w = img.naturalWidth, h = img.naturalHeight;
				if ( ! w || ! h ) return;
				const scale = Math.min( 80 / w, 80 / h, 1 );
				const cw = Math.max( 1, Math.round( w * scale ) );
				const ch = Math.max( 1, Math.round( h * scale ) );
				const canvas = document.createElement( 'canvas' );
				canvas.width = cw; canvas.height = ch;
				const ctx = canvas.getContext( '2d', { willReadFrequently: true } );
				if ( ! ctx ) return;
				ctx.drawImage( img, 0, 0, cw, ch );
				const { data } = ctx.getImageData( 0, 0, cw, ch );
				let clear = 0, lumSum = 0, lumN = 0;
				for ( let i = 0; i < data.length; i += 4 ) {
					const a = data[ i + 3 ];
					if ( a < 16 ) { clear++; continue; }
					if ( a > 200 ) {
						lumSum += 0.2126 * data[ i ] + 0.7152 * data[ i + 1 ] + 0.0722 * data[ i + 2 ];
						lumN++;
					}
				}
				const transparentPct = clear / ( cw * ch );
				const avgLum = lumN ? lumSum / lumN : 255;
				// >8% genuinely transparent AND the visible artwork is dark enough
				// that it would disappear on the near-black backdrop.
				if ( ! cancelled ) setNeedsLight( transparentPct > 0.08 && avgLum < 140 );
			} catch ( e ) {
				if ( ! cancelled ) setNeedsLight( false );
			}
		};
		img.onerror = () => { if ( ! cancelled ) setNeedsLight( false ); };
		img.src = src;
		return () => { cancelled = true; };
	}, [ src ] );

	return needsLight;
}

export default function Lightbox( { src, alt, onClose } ) {
	const [ zoomed, setZoomed ] = useState( false );
	const needsLight = useNeedsLightCanvas( src );

	// Reset zoom every time a new image opens.
	useEffect( () => {
		setZoomed( false );
	}, [ src ] );

	useEffect( () => {
		if ( ! src ) return;
		const handleKey = ( e ) => {
			if ( e.key === 'Escape' ) onClose();
		};
		document.addEventListener( 'keydown', handleKey );
		const prevOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return () => {
			document.removeEventListener( 'keydown', handleKey );
			document.body.style.overflow = prevOverflow;
		};
	}, [ src, onClose ] );

	return (
		<AnimatePresence>
			{ src ? (
				<motion.div
					key="lightbox"
					initial={ { opacity: 0 } }
					animate={ { opacity: 1 } }
					exit={ { opacity: 0 } }
					transition={ { duration: 0.22, ease: [ 0.16, 1, 0.3, 1 ] } }
					className={ `fixed inset-0 z-[200] bg-black/95 backdrop-blur-md ${ zoomed ? 'overflow-auto' : 'flex items-center justify-center p-4 md:p-8' }` }
					onClick={ onClose }
					role="dialog"
					aria-modal="true"
					aria-label="Image preview"
				>
					{ /* Close button — fixed-positioned so it stays visible when scrolling a zoomed image. */ }
					<motion.button
						type="button"
						onClick={ ( e ) => { e.stopPropagation(); onClose(); } }
						initial={ { opacity: 0, y: -8 } }
						animate={ { opacity: 1, y: 0 } }
						exit={ { opacity: 0 } }
						transition={ { duration: 0.28, delay: 0.08 } }
						className="fixed right-5 top-5 z-10 inline-flex h-12 w-12 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white backdrop-blur transition hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white md:right-8 md:top-8"
						aria-label="Close image preview"
					>
						<CloseIcon />
					</motion.button>

					{ /* Image — Fit mode (default) centers via flex parent and caps at
					     92vh × 92vw so the whole image fits. Zoomed mode caps width at
					     the viewport (100vw) and lets height scale freely, so tall page
					     screenshots fill the screen horizontally and the user scrolls
					     vertically to read top→bottom. We never upscale past natural
					     size: `w-auto` keeps the image's intrinsic width as the upper
					     bound, and `max-w-[100vw]` clamps it to the viewport on screens
					     narrower than the image. Updated 2026-05-12 — page screenshots
					     are now 1800px wide and overflowed most viewports horizontally
					     under the old `max-w-none` zoom rule. */ }
					{ /* The canvas wrapper is `w-fit`, so on an opaque image it hugs the
					     image exactly and is never visible; on transparent dark artwork it
					     becomes the white sheet the art reads against. */ }
					{ zoomed ? (
						<div className="flex min-h-full w-full items-start justify-center">
							<motion.div
								key="img-zoomed"
								initial={ { opacity: 0 } }
								animate={ { opacity: 1 } }
								exit={ { opacity: 0 } }
								transition={ { duration: 0.22 } }
								className="w-fit flex-shrink-0"
								style={ needsLight ? { background: '#ffffff' } : undefined }
							>
								<img
									src={ src }
									alt={ alt || '' }
									className="block h-auto w-auto max-w-[100vw] cursor-zoom-out"
									onClick={ ( e ) => { e.stopPropagation(); setZoomed( false ); } }
									draggable={ false }
								/>
							</motion.div>
						</div>
					) : (
						<motion.div
							key="img-fit"
							initial={ { opacity: 0, scale: 0.96 } }
							animate={ { opacity: 1, scale: 1 } }
							exit={ { opacity: 0, scale: 0.96 } }
							transition={ { duration: 0.32, ease: [ 0.16, 1, 0.3, 1 ] } }
							className="w-fit rounded-[1.5rem] shadow-[0_60px_140px_rgba(0,0,0,0.55)]"
							style={ needsLight ? { background: '#ffffff' } : undefined }
						>
							<img
								src={ src }
								alt={ alt || '' }
								className="block max-h-[92vh] max-w-[92vw] cursor-zoom-in rounded-[1.5rem] object-contain"
								onClick={ ( e ) => { e.stopPropagation(); setZoomed( true ); } }
								draggable={ false }
							/>
						</motion.div>
					) }

					{ /* Hint — fixed so it stays visible when zoomed image is scrolling. */ }
					<motion.div
						initial={ { opacity: 0, y: 8 } }
						animate={ { opacity: 1, y: 0 } }
						exit={ { opacity: 0 } }
						transition={ { duration: 0.28, delay: 0.12 } }
						className="pointer-events-none fixed bottom-6 left-1/2 -translate-x-1/2 text-[11px] font-black uppercase tracking-[0.22em] text-white/55"
					>
						{ zoomed ? 'Click image to fit · ESC to close' : 'Click image to zoom · ESC to close' }
					</motion.div>
				</motion.div>
			) : null }
		</AnimatePresence>
	);
}
